import { useMemo } from "react";
import { Dispatch, AnyAction } from "redux";
import { useDispatch } from "react-redux";

import useEligibilityMetaData from "./useEligibilityMetaData";

import {
  EligibilityStatus,
  ReceivableStatus,
  KYCStatus,
  VirtualAccountStatus
} from "../../services/authService/AuthService.type";
import { authActions, UpdateStatus } from "../../store/auth/actions";

interface ActionDispatchers {
  updateStatus: (newStatus: UpdateStatus) => void;
  updateGstinStatus: (isInactive: boolean) => void;
}

const getIndex = (status: EligibilityStatus | null) =>
  status ? Object.values(EligibilityStatus).indexOf(status) : -1;

/**
 * Add any flags to be used throughout the application which depends on Eligibility Status.
 */
const useLoanStatus = () => {
  const dispatch = useDispatch<Dispatch<AnyAction>>();
  const actionDispatchers = useMemo(() => actions(dispatch), [dispatch]);

  const {
    eligibilityMetaData: { status, subStatus }
  } = useEligibilityMetaData();

  const statusDetails = useMemo(
    () => ({
      status,
      subStatus,

      //Eligibility flags
      disableEligibilityForms:
        getIndex(status) >= getIndex(EligibilityStatus.AwaitingPrincipleAmount),
      disableManualCalculationForm:
        getIndex(status) >= getIndex(EligibilityStatus.AwaitingPrincipleAmount),
      // navigation flags
      disableBillsPage:
        getIndex(status) < getIndex(EligibilityStatus.ReadyForDisbursal) ||
        status === EligibilityStatus.ApplicationClosed,

      //eligibilityAmountDetails flags
      showInstantCalculationHelp:
        status === EligibilityStatus.EligibilityAmountFold,
      showAwaitingInPrincipleMessage:
        status === EligibilityStatus.AwaitingPrincipleAmount,
      showAcceptReject: status === EligibilityStatus.PrincipleAmountShared,
      showEligibilityAmount:
        getIndex(status) >= getIndex(EligibilityStatus.PrincipleAmountShared),
      showInsufficientGSTRDataMessage:
        status === EligibilityStatus.InsufficientGSTRData,
      rejectedPrincipalAmount:
        status === EligibilityStatus.PrincipleAmountRejected,

      //eligibility navigation
      disableAccountDetailsPage:
        getIndex(status) < getIndex(EligibilityStatus.PrincipleAmountAccepted),
      disableKycPage:
        getIndex(status) < getIndex(EligibilityStatus.DocumentUploadPending),

      // Home page flags
      showUpdatedReceivables:
        subStatus.receivablesStatus === ReceivableStatus.AwaitingAcceptance,
      isCreditApproved:
        getIndex(status) >= getIndex(EligibilityStatus.CreditApproved),
      inEligibilityFolds:
        getIndex(status) <
          getIndex(EligibilityStatus.PrincipleAmountRejected) &&
        status !== EligibilityStatus.AwaitingPrincipleAmount,
      enableStatusCard:
        getIndex(status) >= getIndex(EligibilityStatus.PrincipleAmountAccepted),
      showKycReUpload: subStatus.kycStatus === KYCStatus.KYCOnHold,
      showAccountCreationUpload:
        subStatus.disbursalBankAccountStatus ===
        VirtualAccountStatus.UploadPending,
      showDisbursalAccountDetailsCard:
        subStatus.disbursalBankAccountStatus ===
          VirtualAccountStatus.UploadPending ||
        subStatus.disbursalBankAccountStatus ===
          VirtualAccountStatus.InVerification,
      hideEligibilityCardButton:
        status === EligibilityStatus.PrincipleAmountRejected ||
        status === EligibilityStatus.OpportunityLost ||
        status === EligibilityStatus.ApplicationClosed ||
        getIndex(status) >=
          getIndex(EligibilityStatus.ApplicationVerificationInProgress),
      isApplicationClosed: status === EligibilityStatus.ApplicationClosed,

      // account details
      disableAccountDetails:
        getIndex(status) >=
        getIndex(EligibilityStatus.ApplicationVerificationInProgress),

      // KYC
      disableKYC:
        getIndex(status) >=
          getIndex(EligibilityStatus.ApplicationVerificationInProgress) &&
        subStatus.kycStatus !== KYCStatus.KYCOnHold,
      dmiInitiated: getIndex(status) >= getIndex(EligibilityStatus.DMIInitiated)
    }),
    [status, subStatus]
  );

  return useMemo(() => ({ ...actionDispatchers, ...statusDetails }), [
    actionDispatchers,
    statusDetails
  ]);
};

const actions = (dispatch: Dispatch<AnyAction>): ActionDispatchers => ({
  updateStatus: (newStatus: UpdateStatus) => {
    dispatch(authActions.updateStatus(newStatus));
  },
  updateGstinStatus: (isInactive: boolean) => {
    dispatch(authActions.updateGstinStatus(isInactive));
  }
});

export default useLoanStatus;
